/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';

import { revealAnchorWhenPresent } from './row-open-request';

/**
 * Scroll into view whatever an action just opened — guidance §22, "the next expected
 * action is in view" (#922 · 3).
 *
 * The mapping editors are tall by construction: a real ORTB body runs to dozens of
 * leaves across eight sections, and a demand source can carry forty parameters. So
 * *below the fold* is the normal case, and adding a field at the end of a list, opening
 * a row's editor or expanding an expression panel routinely puts the control the
 * operator needs next somewhere they cannot see.
 *
 * The failure mode is what makes this worth a shared implementation rather than four
 * `scrollIntoView` calls: an operator who clicks **Add** and sees nothing change does
 * not scroll — they conclude the click did not register and click again. **The absence
 * of feedback is read as the absence of an effect**, and the surface acquires duplicate
 * rows and a reputation for being unresponsive.
 *
 * Three decisions, all of which are easy to get subtly wrong alone:
 *
 * - **`block: 'nearest'`.** A target already fully visible must not move. Scrolling for
 *   its own sake is its own disorientation and is what makes a list feel like it is
 *   fighting the operator. `'start'` is right only for a jump between distant surfaces
 *   — the detail page's Fix anchor — not for a row a few hundred pixels down.
 * - **One frame late.** The element being revealed is usually rendered by the same state
 *   change that requests the reveal, so it does not exist when the handler runs. Keying
 *   the effect on the *token* lets React commit first.
 * - **Only on a change of token, never on mount or on re-render.** A component that
 *   scrolls whenever it renders fights every unrelated update, and that is far harder to
 *   notice than a missing scroll — it presents as the page "jumping" for no reason.
 *
 * Usage: hold a token identifying what is open (a row id, a section name, `null` for
 * nothing), put the returned ref on that element, and the reveal follows the token.
 *
 * ```tsx
 * const revealRef = useRevealOnOpen(editingId);
 * …
 * <MappingRow ref={editing ? revealRef : undefined} …/>
 * ```
 */
/**
 * Does this token change earn a scroll? The whole of the hook's judgment, pulled out
 * so it can be asserted without a DOM.
 *
 * - **`undefined` previous means first render** — a surface that opens with something
 *   already expanded (a deep link, a staged Fix) must not yank the page before the
 *   operator has seen it. An *action* earns the scroll, not a mount.
 * - **`null` next means nothing is open**, so there is nothing to reveal. Closing a row
 *   must not scroll: the operator's eye is already where they left it.
 * - **An unchanged token is a re-render**, and scrolling on every render is what makes a
 *   page jump for no reason — much harder to spot than a missing scroll, because it
 *   looks like a general flakiness rather than one broken interaction.
 */
export function shouldReveal(previous: string | null | undefined, next: string | null): boolean {
  if (previous === undefined) return false;
  if (next === null) return false;
  return previous !== next;
}

export function useRevealOnOpen<T extends HTMLElement = HTMLDivElement>(
  token: string | null,
): React.RefObject<T | null> {
  const ref = React.useRef<T | null>(null);
  // `undefined` rather than `null` as the initial value, so the very first render of a
  // surface that opens with something already expanded does not count as a change. A
  // deep link that lands on an open row should not yank the page before the operator
  // has seen it — the *action* is what earns a scroll.
  const previous = React.useRef<string | null | undefined>(undefined);

  React.useEffect(() => {
    const reveal = shouldReveal(previous.current, token);
    previous.current = token;
    if (!reveal) return;

    // The element is rendered by the same commit that changed the token, so it exists
    // by the time this effect runs — but a nested editor may still be laying out. One
    // frame is enough and costs nothing when it was not needed.
    const frame = requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [token]);

  return ref;
}

/**
 * **Reveal a section by its DOM anchor, after the layout that opens it has settled** (`#1254`).
 *
 * The sibling above reveals an element this component renders, so a ref reaches it. This one
 * reveals a section **somewhere else on the page** — the detail page's Fix jumps from a panel at
 * the top to an editor that is about to remount — so the only handle is the anchor id, and the
 * element does not exist yet when the gesture fires.
 *
 * ## Why it is an effect and not a frame
 *
 * Todd, reporting it: *"sometimes clicking it doesn't scroll."* The old code set two pieces of state
 * in an event handler and scheduled the scroll on the **next frame** — but React has not necessarily
 * re-rendered by then and the section has not necessarily expanded, so the scroll targeted a
 * position that was about to move. **One `requestAnimationFrame` is a guess at React's timing, not a
 * wait for it**, and intermittency is the signature of a guess. Keying an effect on the token is the
 * same fix `useRevealOnOpen` already documents one function up.
 *
 * ## And it waits for the element rather than swallowing its absence
 *
 * `document.getElementById(anchor)?.scrollIntoView(…)` fails silently on the `?.`, and a section
 * that is remounting under a nonce legitimately is not there on the first frame. So it retries for a
 * bounded handful of frames and then **says so** — to the console, because an operator-facing
 * sentence about a missing DOM id would be naming a mechanism rather than describing an outcome
 * (`#1045`). The staged change has landed either way; what failed is the convenience.
 *
 * That waiting is `revealAnchorWhenPresent` since `#1305` gave it a second caller — the row reveal,
 * which faces the same two absences for the same two reasons.
 */
export interface RevealRequest {
  anchorId: string;
  /**
   * Distinguishes two requests for the **same** anchor, which is an ordinary case — two Fixes on
   * one editor. Without it the second reveals nothing, because the token has not changed.
   */
  requestId: number;
}

export function useRevealAnchor(request: RevealRequest | null): void {
  const previous = React.useRef<string | null | undefined>(undefined);
  const token = request ? `${request.anchorId}#${request.requestId}` : null;
  const anchorId = request?.anchorId ?? null;

  React.useEffect(() => {
    const reveal = shouldReveal(previous.current, token);
    previous.current = token;
    if (!reveal || anchorId === null) return;

    // `'start'` because this is a jump between distant surfaces — a panel at the top of the page to
    // an editor further down. The row reveal one layer in uses `'nearest'`, for the reason
    // `useRevealOnOpen` documents above.
    return revealAnchorWhenPresent(anchorId, 'start');
    // `token` is the dependency; `anchorId` is derived from the same request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
}
